import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { listInsuranceCatalogFn, insuranceStatusFor, SELF_PAY_ID } from "@/lib/insurance-catalog.functions";
import { InsurancePickerDialog } from "@/components/forms/InsurancePickerDialog";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import {
  findInsurance,
  findProvidersByIdentifier,
  rankProvider,
  parseLocationToCityState,
  SPECIALTIES,
  CITIES,
  INSURANCES,
  cacheLastSearchResults,
  type Provider,
} from "@/data/heallexa";
import { aiParseSearch } from "@/lib/ai-search.functions";
import { buildProviderSlug } from "@/lib/provider-slug";
import { stateFromNameSlug } from "@/lib/seo-slugs";
import type { SearchLoaderResult } from "@/lib/search-loader";
import { resolveCityFromLabel, type ResolvedCity } from "@/lib/providers-nav";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Sparkles, Loader2, SlidersHorizontal, Search as SearchIcon, ArrowRight, ChevronLeft, ChevronRight, ChevronDown, X } from "lucide-react";
import { getGeoStatus, subscribeUserCoords, type GeoStatus } from "@/lib/user-location";
import { ProviderCard } from "@/components/search/ProviderCard";
import { useI18n } from "@/i18n/I18nProvider";
import { BackButton } from "@/components/BackButton";
import { LocationAutocomplete } from "@/components/forms/LocationAutocomplete";
import { UseMyLocationButton } from "@/components/search/UseMyLocationButton";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import type { LocationSuggestion } from "@/lib/location-autocomplete";

// Route-agnostic core of the search UI, shared by /search, /providers/$id
// (specialty branch), and /providers/$state/$city — each route wrapper owns
// its own useSearch/useNavigate and decides what a committed filter change
// means for ITS URL (stay + update a query param, or navigate elsewhere);
// this component just reports commits via callbacks. Rating/gender/sort/
// verified/premium/accepting-new stay pure internal state here, matching
// /search's pre-existing behavior — they were never URL-synced even on
// /search itself, so there's nothing to thread through props for them.

type AiResult = {
  specialty: string;
  city: string;
  insurance: string;
  keywords: string[];
  summary: string;
  intent: string;
  lang?: "en" | "es";
};

function scoreProvider(p: Provider, ai: AiResult | null): number {
  let score = rankProvider(p) * 10;
  score += (Number(p.rating) || 0);
  if (!ai) return score;
  const hay = [
    p.name, p.practice, p.specialty, p.city, p.state ?? "",
    p.about ?? "", (p.services || []).join(" "), (p.languages || []).join(" "),
  ].join(" ").toLowerCase();
  if (ai.specialty && p.specialty.toLowerCase().includes(ai.specialty.toLowerCase())) score += 50;
  if (ai.city && p.city.toLowerCase().includes(ai.city.split(",")[0].toLowerCase())) score += 40;
  if (ai.insurance) {
    const ins = INSURANCES.find(
      (i) => i.short.toLowerCase() === ai.insurance.toLowerCase() || i.name.toLowerCase() === ai.insurance.toLowerCase()
    );
    if (ins && (p.insurance || []).includes(ins.id)) score += 30;
  }
  for (const kw of ai.keywords) {
    if (kw && hay.includes(kw.toLowerCase())) score += 8;
  }
  return score;
}

/**
 * Keywords worth showing as "why this matched" chips.
 *
 * Only the ones the card does NOT already display. Searching a doctor by name
 * used to render a chip repeating the name directly under the heading — the
 * same words twice, which reads as a bug. A chip earns its space only when it
 * surfaces something otherwise invisible, like a service or a phrase from the
 * provider's bio.
 */
function matchedKeywords(p: Provider, ai: AiResult | null): string[] {
  if (!ai) return [];
  const onCard = [p.name, p.specialty, p.city, (p.languages || []).join(" ")]
    .join(" ")
    .toLowerCase();
  const hidden = [p.practice, p.about ?? "", (p.services || []).join(" ")].join(" ").toLowerCase();
  return ai.keywords.filter((k) => {
    const key = k?.toLowerCase();
    return key && hidden.includes(key) && !onCard.includes(key);
  });
}

// NOTE: there is deliberately no client-side specialty filter here.
//
// There used to be one, re-testing each result against a regex built from
// SPECIALTY_DB_PATTERNS. It has to go: the server now resolves a specialty
// through the canonical taxonomy (which knows an endodontist is a dentist and
// that "retina" means ophthalmology), so re-filtering client-side with a
// string pattern would throw away exactly the providers the taxonomy just
// recovered — a "Dentists" search would drop every endodontist again.
//
// Changing the specialty always commits and refetches (onSpecialtyCommit), so
// the rows in hand are already scoped to the selected specialty server-side.

export type { ResolvedCity };

export type SearchExperienceProps = {
  loaderData: SearchLoaderResult;
  initial: { q: string; location: string; radius: number };
  isRouteLoading: boolean;
  onQueryChange: (q: string) => void;
  onLocationSubmit: (location: string) => void;
  onSpecialtyCommit: (specialty: string, currentCity: string) => void;
  onCityCommit: (cityLabel: string, matched: ResolvedCity | null, currentSpecialty: string) => void;
  onRadiusChange: (radius: number) => void;
  onPageChange: (page: number) => void;
  /**
   * "Near me" support, supplied only by routes that can actually act on device
   * coordinates (today: /search, which owns lat/lng search params). Omitted
   * elsewhere, and the control hides itself rather than offering a button that
   * would do nothing. `nearMe` is whether the CURRENT results are geo-scoped.
   */
  nearMe?: boolean;
  onUseMyLocation?: () => void;
  onClearMyLocation?: () => void;
  /** Extra content (SEO copy, FAQ, etc.) rendered after the results, before the footer. */
  belowResults?: React.ReactNode;
};

const PAGE_SIZE = 20;

export function SearchExperience({
  loaderData,
  initial,
  isRouteLoading,
  onQueryChange,
  onLocationSubmit,
  onSpecialtyCommit,
  onCityCommit,
  onRadiusChange,
  onPageChange,
  nearMe,
  onUseMyLocation,
  onClearMyLocation,
  belowResults,
}: SearchExperienceProps) {
  const { results, filters, aiQuery, ai: loaderAi, meta, page: loaderPage, hasMore, total, totalCapped } = loaderData;
  const { t } = useI18n();
  const [ai, setAi] = useState<AiResult | null>(loaderAi as AiResult | null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Debounced search input that drives the `q` URL param.
  const [queryDraft, setQueryDraft] = useState<string>(initial.q);
  const lastSubmittedRef = useRef<string>(initial.q);
  useEffect(() => {
    setQueryDraft(initial.q);
    lastSubmittedRef.current = initial.q;
  }, [initial.q]);
  useEffect(() => {
    const next = queryDraft.trim();
    if (next === (lastSubmittedRef.current ?? "")) return;
    const handle = setTimeout(() => {
      lastSubmittedRef.current = next;
      onQueryChange(next);
    }, 400);
    return () => clearTimeout(handle);
  }, [queryDraft, onQueryChange]);

  // Top search-bar location field — submits on Enter/click (not debounced,
  // since a location change triggers a full server round-trip via the loader).
  const [locationDraft, setLocationDraft] = useState<string>(initial.location);
  useEffect(() => {
    setLocationDraft(initial.location);
  }, [initial.location]);
  const submitLocationSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onLocationSubmit(locationDraft.trim());
  };

  // Client-side refine filters — seed from the loader's echoed filters so the
  // panel reflects deep links (path segment or query param, whichever route
  // this is).
  // The URL is the source of truth for the insurance filter, so it survives
  // pagination and is shareable. `filters.insurance` is usually already a token
  // ("aetna", "aetna:choice-pos-ii", "selfpay") — pass those through untouched,
  // because matching against the static 51-carrier INSURANCES list would drop
  // every plan token and every one of the 500+ catalog-only carriers. The
  // name/short lookup stays as a fallback for AI-parsed queries, where the value
  // arrives as a display name ("Blue Cross") rather than an id.
  const initialInsuranceId = useMemo(() => {
    const v = (filters.insurance || "").trim().toLowerCase();
    if (!v) return "any";
    if (/^[a-z0-9-]+(:[a-z0-9-]+)?$/.test(v)) return v;
    const match = INSURANCES.find(
      (i) => i.short.toLowerCase() === v || i.name.toLowerCase() === v,
    );
    return match?.id ?? "any";
  }, [filters.insurance]);
  const [fSpecialty, setFSpecialty] = useState<string>(filters.specialty || "any");
  const [fCity, setFCity] = useState<string>(filters.location || "any");
  const [fInsurance, setFInsurance] = useState<string>(initialInsuranceId);
  // Carrier + plan share one filter value: a plan token embeds its carrier
  // ("aetna:choice-pos-ii"), so the carrier dropdown shows the prefix and the
  // plan dropdown the whole token. No second URL param, no server change.
  const insuranceCarrier = fInsurance.includes(":") ? fInsurance.split(":")[0] : fInsurance;
  const { data: insuranceCatalog } = useQuery({
    queryKey: ["insurance-catalog"],
    queryFn: () => listInsuranceCatalogFn(),
    staleTime: 5 * 60_000,
  });
  // Falls back to the static list until the catalog tables are seeded, so the
  // carrier filter never renders empty.
  const insuranceCarriers = insuranceCatalog?.carriers?.length
    ? insuranceCatalog.carriers
    : INSURANCES.filter((i) => i.id !== SELF_PAY_ID);
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);

  // Write the choice to the URL rather than keeping it in component state:
  // pagination navigates, which re-runs the loader and re-seeds every filter
  // from `filters` — a state-only value was silently dropped on page 2. All
  // four routes that render this already accept `insurance` in validateSearch.
  // Local state is set too so the chip updates before the loader answers.
  const navigate = useNavigate();
  const selectInsurance = (token: string | null) => {
    setFInsurance(token ?? "any");
    // `as any`: this component is rendered by four different routes, so there is
    // no single `from` to type the search shape against. Every one of them
    // declares `insurance` and `page` in validateSearch.
    navigate({
      // A new filter invalidates the current page offset — go back to page 1.
      search: ((prev: any) => ({ ...prev, insurance: token ?? undefined, page: undefined })) as any,
      replace: true,
    });
  };

  // Chip text: the plan name when one is chosen, else the carrier, else the
  // generic filter label — so the chip always says what is actually filtering.
  const insuranceChipLabel = useMemo(() => {
    if (fInsurance === "any") return t.filters.insurance;
    if (fInsurance === SELF_PAY_ID) return "Paying for myself";
    const plan = (insuranceCatalog?.plans ?? []).find((p) => p.id === fInsurance);
    if (plan) return plan.name;
    return insuranceCarriers.find((c) => c.id === insuranceCarrier)?.name ?? t.filters.insurance;
  }, [fInsurance, insuranceCatalog, insuranceCarriers, insuranceCarrier, t.filters.insurance]);

  const [fMinRating, setFMinRating] = useState<string>("any");
  const [fVerified, setFVerified] = useState(false);
  const [fPremium, setFPremium] = useState(false);
  const [fAcceptingNew, setFAcceptingNew] = useState(false);
  const [fGender, setFGender] = useState<string>("any");
  const [fSort, setFSort] = useState<string>("relevance");
  // When the visitor's location is known, results carry distances → default to
  // sorting by distance. Stops once they pick a sort themselves.
  const sortTouched = useRef(false);
  useEffect(() => {
    if (sortTouched.current) return;
    setFSort(results.some((p) => (p as any).distance_m != null) ? "distance" : "relevance");
  }, [results]);

  // Keep panel in sync when the loader re-runs with different filters (e.g.
  // browser back/forward).
  useEffect(() => {
    setFSpecialty(filters.specialty || "any");
    setFCity(filters.location || "any");
    setFInsurance(initialInsuranceId);
  }, [filters.specialty, filters.location, initialInsuranceId]);

  // The city filter chip used to only narrow the already-loaded page of
  // results client-side (so picking a city could show as few as 1 provider —
  // whatever happened to already be in that batch). Debounce it into a real
  // commit instead, same as queryDraft does for `q`. When the typed text
  // unambiguously matches one of Heallexa's own known cities, resolve it to
  // {city, state} so the caller can jump to a clean URL — this doesn't depend
  // on the counts-autocomplete dropdown (which needs a DB migration that
  // hasn't been applied yet) since it's resolved against the same static
  // city list the /providers pages use.
  const lastSubmittedCityRef = useRef<string>(filters.location || "");
  useEffect(() => {
    const next = fCity === "any" ? "" : fCity.trim();
    if (next === (lastSubmittedCityRef.current ?? "")) return;
    const handle = setTimeout(() => {
      lastSubmittedCityRef.current = next;
      onCityCommit(next, next ? resolveCityFromLabel(next) : null, fSpecialty);
    }, 400);
    return () => clearTimeout(handle);
  }, [fCity, fSpecialty, onCityCommit]);

  const resetFilters = () => {
    setFSpecialty("any"); setFCity("any"); selectInsurance(null);
    setFMinRating("any"); setFVerified(false); setFPremium(false);
    setFAcceptingNew(false); setFGender("any"); setFSort("relevance");
  };

  const activeFilterCount = [
    fSpecialty !== "any", fCity !== "any", fInsurance !== "any", fMinRating !== "any",
    fVerified, fPremium, fAcceptingNew, fGender !== "any",
  ].filter(Boolean).length;
  const anyFilterActive = activeFilterCount > 0;
  const activeChips = [
    fSpecialty !== "any" && { key: "specialty", label: fSpecialty, clear: () => setFSpecialty("any") },
    fCity !== "any" && { key: "city", label: fCity, clear: () => setFCity("any") },
    fInsurance !== "any" && { key: "insurance", label: insuranceChipLabel, clear: () => selectInsurance(null) },
    fMinRating !== "any" && { key: "rating", label: `${fMinRating}+ rating`, clear: () => setFMinRating("any") },
    fGender !== "any" && { key: "gender", label: fGender === "f" ? "Female" : "Male", clear: () => setFGender("any") },
    fVerified && { key: "verified", label: t.filters.verifiedOnly, clear: () => setFVerified(false) },
    fPremium && { key: "premium", label: t.filters.premiumOnly, clear: () => setFPremium(false) },
    fAcceptingNew && { key: "accepting", label: "Accepting new", clear: () => setFAcceptingNew(false) },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];
  // Mobile only: the chip row is ~5 wrapped rows on a phone, so it collapses
  // behind one "Filters" button. On md+ CSS forces it open and this is unused.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Background AI parse: results already rendered from the loader's
  // fast local-parse path. The LLM call refines filter chips and
  // ranking without blocking the initial render.
  useEffect(() => {
    setAi((loaderAi as AiResult | null) ?? null);
    setAiError(null);
    if (!aiQuery) { setAiLoading(false); return; }
    if (loaderAi) { setAiLoading(false); return; }
    let cancelled = false;
    setAiLoading(true);
    aiParseSearch({
      data: {
        query: aiQuery,
        specialties: SPECIALTIES,
        cities: CITIES,
        insurances: INSURANCES.map((i) => i.short),
      },
    })
      .then((resp) => { if (!cancelled) setAi((resp.result as AiResult | null) ?? null); })
      .catch((e) => { if (!cancelled) setAiError(e?.message ?? "AI unavailable"); })
      .finally(() => { if (!cancelled) setAiLoading(false); });
    return () => { cancelled = true; };
  }, [loaderAi, aiQuery]);

  const ranked = useMemo(() => {
    let base: Provider[];
    if (!aiQuery) base = results;
    else {
      const withScores = results.map((p: Provider) => ({ p, s: scoreProvider(p, ai) }));
      withScores.sort((a: { p: Provider; s: number }, b: { p: Provider; s: number }) => b.s - a.s);
      if (ai && (ai.specialty || ai.city || ai.insurance || ai.keywords.length)) {
        const filtered = withScores.filter(({ p }: { p: Provider; s: number }) => {
          const specOk = !ai.specialty || p.specialty.toLowerCase().includes(ai.specialty.toLowerCase());
          const aiParsed = parseLocationToCityState(ai.city);
          const cityOk = !aiParsed.city || p.city.toLowerCase().includes(aiParsed.city.toLowerCase());
          const stateOk = !aiParsed.state || (p.state || "").toUpperCase() === aiParsed.state;
          return specOk && cityOk && stateOk;
        });
        const list = filtered.length > 0 ? filtered : withScores.slice(0, 12);
        base = list.map((x: { p: Provider; s: number }) => x.p);
      } else {
        base = withScores.map((x: { p: Provider; s: number }) => x.p);
      }
    }
    return base.filter((p) => {
      // A bare ZIP is already resolved to a real city/state server-side, so the
      // rows are correctly scoped; re-filtering here on the literal digits
      // matched no provider's city and dropped every result.
      if (fCity !== "any" && !/^\d{5}$/.test(fCity.trim())) {
        const parsed = parseLocationToCityState(fCity);
        if (parsed.city && !p.city.toLowerCase().includes(parsed.city.toLowerCase())) return false;
        if (parsed.state && (p.state || "").toUpperCase() !== parsed.state) return false;
      }
      // Insurance is NOT a client-side filter any more. The server ranks
      // in-network -> out-of-network -> unknown (public.insurance_rank) and
      // returns all three, so dropping non-matches here would hide the two
      // tiers the badges exist to explain — and with most listings carrying
      // an empty insurance[], it hid nearly the whole directory.
      if (fMinRating !== "any" && (Number(p.rating) || 0) < Number(fMinRating)) return false;
      if (fVerified && p.listing_status === "unclaimed") return false;
      if (fPremium && p.listing_status !== "premium") return false;
      if (fAcceptingNew) {
        const a = ((p as any).availability || "").toLowerCase();
        const hasDays = ((p as any).available_days || []).length > 0;
        const accepting = a
          ? !/(not accepting|closed to new|waitlist|full)/.test(a)
          : hasDays;
        if (!accepting) return false;
      }
      if (fGender !== "any") {
        const g = String((p as any).gender || "").toLowerCase();
        if (!g.startsWith(fGender)) return false;
      }
      return true;
    })
      .slice()
      .sort((a, b) => {
        // Insurance tier outranks every other sort: in-network (0) ->
        // out-of-network (1) -> unknown (2). Ties fall through to the chosen
        // sort below, so "Aetna + highest rated" still means the best-rated
        // in-network doctor first.
        //
        // Client-side because insurance is no longer a SQL predicate (see
        // search-loader). That makes ordering exact within a page and
        // approximate across pages — the server still pages by rating, so a
        // late-page in-network provider cannot jump to page 1.
        if (fInsurance !== "any") {
          const rank = (p: Provider) => {
            const status = insuranceStatusFor(p.insurance, fInsurance);
            return status === "in" ? 0 : status === "out" ? 1 : 2;
          };
          const d = rank(a) - rank(b);
          if (d !== 0) return d;
        }
        if (fSort === "rating") return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        if (fSort === "reviews") return (Number((b as any).reviews_count) || 0) - (Number((a as any).reviews_count) || 0);
        if (fSort === "distance") {
          return (Number((a as any).distance_m) || Infinity) - (Number((b as any).distance_m) || Infinity);
        }
        return 0;
      });
    // fSpecialty is deliberately absent: specialty filtering happens server-side
    // (changing it refetches via onSpecialtyCommit), so recomputing this memo on
    // it would be a no-op.
  }, [results, ai, aiQuery, fCity, fInsurance, fMinRating, fVerified, fPremium, fAcceptingNew, fGender, fSort]);

  useEffect(() => {
    cacheLastSearchResults(ranked);
  }, [ranked]);

  const resultsTitle = initial.q
    ? `"${initial.q}"`
    : filters.specialty && filters.location
    ? `${filters.specialty} in ${filters.location}`
    : filters.specialty
    ? filters.specialty
    : filters.location
    ? `Providers in ${filters.location}`
    : "All providers";

  const countLabel = ranked.length === 0
    ? "No providers found"
    : (() => {
      const startIdx = (loaderPage - 1) * PAGE_SIZE + 1;
      const endIdx = (loaderPage - 1) * PAGE_SIZE + ranked.length;
      const totalLabel = total > 0
        ? `${total.toLocaleString()}${totalCapped ? "+" : ""}`
        : `${endIdx}${hasMore ? "+" : ""}`;
      return `${startIdx}–${endIdx} of ${totalLabel} providers found`;
    })();

  return (
    <div className="min-h-screen bg-background pb-1">
      <Navbar />
      <header className="pt-[72px] md:pt-20 pb-3 bg-background">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <BackButton className="mb-1.5 md:mb-2 -ml-2" />

          {/* One search bar: location + query + submit share a single box on
              every breakpoint, and the "find a specific provider" panel that
              used to sit under it is now a typeahead inside the query field —
              same feature, one control instead of two stacked blocks. */}
          <SearchBar
            locationDraft={locationDraft}
            onLocationDraftChange={setLocationDraft}
            queryDraft={queryDraft}
            onQueryDraftChange={setQueryDraft}
            onSubmit={submitLocationSearch}
            loading={isRouteLoading}
            nearMe={nearMe}
            onUseMyLocation={onUseMyLocation}
            onClearMyLocation={onClearMyLocation}
          />

          {initial.q && (aiLoading || ai || aiError) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
              {aiLoading ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              )}
              {aiLoading && <span className="text-muted-foreground">Understanding your request…</span>}
              {ai?.summary && <span className="text-foreground/80">{ai.summary}</span>}
              {ai && (
                <span className="flex flex-wrap gap-1.5">
                  {ai.specialty && <FilterPill label={ai.specialty} />}
                  {ai.city && <FilterPill label={ai.city} />}
                  {ai.insurance && <FilterPill label={ai.insurance} />}
                  {ai.intent && <FilterPill label={ai.intent} muted />}
                </span>
              )}
              {aiError && <span className="text-muted-foreground">Showing best keyword matches</span>}
            </div>
          )}
        </div>
      </header>

      {/* FILTER BAR — one collapsed row + sort on mobile, wrapping chip row on md+ */}
      <div className="border-b border-border sticky top-[56px] z-30 bg-background/95 backdrop-blur md:static md:bg-background md:backdrop-blur-none">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-2 md:py-2.5">
        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            className={`flex-1 justify-between px-3 inline-flex items-center gap-2 ${filterChipClass(anyFilterActive)}`}
          >
            <span className="inline-flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" /> {t.filters.title}
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
          <SortSelect
            value={fSort}
            onChange={(v) => { sortTouched.current = true; setFSort(v); }}
            hasDistance={results.some((p) => (p as any).distance_m != null)}
            className="flex-1 min-w-0"
          />
        </div>

        {/* Collapsed state still has to say what is filtering — otherwise the
            count badge is the only clue and clearing means opening the panel. */}
        {!filtersOpen && activeChips.length > 0 && (
          <div className="md:hidden -mx-4 mt-2 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {activeChips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={c.clear}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/40 bg-primary/5 py-1 pl-2.5 pr-2 text-[12px] text-foreground"
              >
                <span className="max-w-[140px] truncate">{c.label}</span>
                <X className="h-3 w-3 text-muted-foreground" aria-hidden />
                <span className="sr-only">Remove filter</span>
              </button>
            ))}
          </div>
        )}

        <div
          className={`${filtersOpen ? "grid grid-cols-2 gap-2 mt-2" : "hidden"} md:mt-0 md:flex md:flex-wrap md:items-center md:gap-2`}
        >
          <span className="hidden md:inline-flex items-center gap-2 text-[13px] text-muted-foreground mr-1 shrink-0">
            <SlidersHorizontal className="h-4 w-4" /> {t.filters.title}
          </span>

          <div className={`w-full md:w-auto md:min-w-[140px] md:max-w-[200px] px-3 flex items-center shrink-0 ${filterChipClass(fSpecialty !== "any")}`}>
            <SpecialtySelect
              value={fSpecialty === "any" ? "" : fSpecialty}
              onChange={(v) => {
                const next = v.trim() ? v : "any";
                setFSpecialty(next);
                if (next !== "any") onSpecialtyCommit(next, fCity);
              }}
              placeholder={t.filters.specialty}
              allowCustom={false}
            />
          </div>

          <div
            className={`w-full md:w-[150px] px-3 flex items-center gap-1.5 shrink-0 focus-within:border-primary/50 focus-within:bg-primary/5 ${filterChipClass(fCity !== "any")}`}
          >
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden />
            <LocationAutocomplete
              value={fCity === "any" ? "" : fCity}
              onChange={(value) => setFCity(value.trim() ? value : "any")}
              onSelect={(s: LocationSuggestion) => {
                if (!s.city || !s.state) return;
                const state = stateFromNameSlug(s.state);
                onCityCommit(
                  [s.city, s.state].join(", "),
                  state ? { city: s.city, state: state.code } : null,
                  fSpecialty,
                );
              }}
              placeholder={t.filters.city}
              className="flex-1 min-w-0"
              inputClassName="h-9 w-full bg-transparent border-0 shadow-none px-0 text-[13px] outline-none focus-visible:ring-0"
              withCounts
            />
          </div>

          {/* `nearMe` counts as a location: device coords ARE the radius centre.
              Without it the control stayed disabled ("Pick a city to enable
              radius") in exactly the mode the radius matters most — the search
              bar swaps the city input for the "Near you" chip, so fCity is
              stuck on "any" and 25 mi could never be changed. */}
          <RadiusFilter
            radius={initial.radius}
            hasLocation={Boolean(nearMe) || Boolean(parseLocationToCityState(fCity === "any" ? "" : fCity).city)}
            onRadiusChange={onRadiusChange}
          />

          {/* One chip opens the carrier→plan dialog. Both levels write the SAME
              `fInsurance` param — a plan token ("aetna:choice-pos-ii") already
              contains its carrier, so the server filter is unchanged and the URL
              keeps one insurance value. Picking a carrier alone still matches
              doctors who listed only specific plans, because the doctor picker
              always writes the carrier token too. */}
          <button
            type="button"
            onClick={() => setInsuranceDialogOpen(true)}
            className={`w-full md:w-auto md:max-w-[240px] md:min-w-[110px] px-3 shrink-0 truncate ${filterChipClass(fInsurance !== "any")}`}
          >
            {insuranceChipLabel}
          </button>
          <InsurancePickerDialog
            open={insuranceDialogOpen}
            onOpenChange={setInsuranceDialogOpen}
            value={fInsurance === "any" ? null : fInsurance}
            onSelect={selectInsurance}
          />

          <Select value={fMinRating} onValueChange={setFMinRating}>
            <SelectTrigger className={`w-full md:w-auto md:min-w-[100px] shrink-0 ${filterChipClass(fMinRating !== "any")}`}>
              <SelectValue placeholder={t.filters.minRating} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t.filters.minRating}</SelectItem>
              <SelectItem value="3">3.0+ rating</SelectItem>
              <SelectItem value="4">4.0+ rating</SelectItem>
              <SelectItem value="4.5">4.5+ rating</SelectItem>
            </SelectContent>
          </Select>

          <Select value={fGender} onValueChange={setFGender}>
            <SelectTrigger className={`w-full md:w-auto md:min-w-[100px] shrink-0 ${filterChipClass(fGender !== "any")}`}>
              <SelectValue placeholder="Gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any gender</SelectItem>
              <SelectItem value="f">Female</SelectItem>
              <SelectItem value="m">Male</SelectItem>
            </SelectContent>
          </Select>

          <ToggleChip active={fVerified} onClick={() => setFVerified((v) => !v)}>{t.filters.verifiedOnly}</ToggleChip>
          <ToggleChip active={fPremium} onClick={() => setFPremium((v) => !v)}>{t.filters.premiumOnly}</ToggleChip>
          <ToggleChip active={fAcceptingNew} onClick={() => setFAcceptingNew((v) => !v)}>Accepting new</ToggleChip>

          {anyFilterActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="col-span-2 md:col-span-1 h-9 px-3 text-[13px] text-muted-foreground hover:text-foreground rounded-md transition-colors shrink-0"
            >
              {t.filters.reset}
            </button>
          )}

          <label className="hidden md:flex ml-auto items-center gap-2 text-[13px] text-muted-foreground shrink-0">
            Sort
            <SortSelect
              value={fSort}
              onChange={(v) => { sortTouched.current = true; setFSort(v); }}
              hasDistance={results.some((p) => (p as any).distance_m != null)}
              className="w-auto min-w-[130px]"
            />
          </label>
        </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1200px] px-4 md:px-6 py-5 md:py-6">
        <div className="mb-4 md:mb-5">
          <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
            {isRouteLoading ? "Searching…" : countLabel}
          </p>
          <h1 className="font-display text-[22px] md:text-[28px] font-semibold tracking-tight text-foreground mt-1.5 md:mt-2">
            {resultsTitle}
          </h1>
          {meta && meta.usedRadius && (
            <p className="mt-2 text-xs text-primary inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              {/* Device "near me" has no typed location, so meta.city is
                  undefined and this used to render "Within 25 mi of  —". */}
              Within {meta.requestedRadiusMiles} mi of{" "}
              {meta.city ? `${meta.city}${meta.state ? `, ${meta.state}` : ""}` : "your location"}
              {" "}— including nearby cities
            </p>
          )}
          {meta && !meta.usedRadius && meta.requestedRadiusMiles > 0 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
              Couldn't pinpoint that city on a map — showing exact-city results only.
            </p>
          )}
          {/* The "Near you" chip in the search bar is driven by the lat/lng in
              the URL, but the loader drops distance scoping whenever the radius
              search fails or comes back empty (see the nationwide fallback in
              search-loader.ts). That fallback resets requestedRadiusMiles to 0,
              so neither banner above fires and the chip is left claiming a
              proximity search that did not happen. Say so instead. */}
          {nearMe && meta && !meta.usedRadius && meta.requestedRadiusMiles === 0 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
              Couldn't search near you just now — showing nationwide results instead.
            </p>
          )}
        </div>

        {ranked.length === 0 ? (
          <div className="card-surface rounded-md p-12 text-center">
            <p className="text-muted-foreground">No providers matched your search. Try broader filters or browse by specialty.</p>
            <Button asChild className="mt-6"><Link to="/browse">Browse all</Link></Button>
          </div>
        ) : (
          <ResultsList
            ranked={ranked}
            ai={ai}
            page={loaderPage}
            hasMore={hasMore}
            total={total}
            onPageChange={onPageChange}
            insuranceToken={fInsurance === "any" ? null : fInsurance}
            insuranceLabel={insuranceChipLabel}
          />
        )}
      </main>

      {belowResults}

      <FooterSection />
    </div>
  );
}

function ResultsList({
  ranked,
  ai,
  page,
  hasMore,
  total,
  onPageChange,
  insuranceToken,
  insuranceLabel,
}: {
  ranked: Provider[];
  ai: AiResult | null;
  page: number;
  hasMore: boolean;
  total: number;
  onPageChange: (page: number) => void;
  /** Selected insurance token, or null when no insurance filter is applied. */
  insuranceToken: string | null;
  insuranceLabel: string;
}) {
  // Derive total pages from the count when available, otherwise grow with the
  // current page + hasMore signal so users can still navigate forward.
  const knownPages = total > 0 ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 0;
  const fallbackPages = page + (hasMore ? 1 : 0);
  const totalPages = Math.max(knownPages, fallbackPages, 1);

  return (
    <div className="grid gap-5">
      {ranked.map((p) => (
        <ProviderCard
          key={p.id}
          p={p}
          highlights={matchedKeywords(p, ai)}
          insuranceToken={insuranceToken}
          insuranceLabel={insuranceLabel}
        />
      ))}

      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center justify-center gap-3 pt-6">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-[13px] text-muted-foreground tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={!hasMore && page >= totalPages}
            className="inline-flex items-center gap-1 text-[13px] h-9 px-3 rounded-md border border-border bg-background disabled:opacity-40 hover:bg-muted transition-colors"
            aria-label="Next page"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}

const RADIUS_OPTIONS = [0, 5, 10, 15, 25, 50];

function RadiusFilter({
  radius,
  hasLocation,
  onRadiusChange,
}: {
  radius: number;
  hasLocation: boolean;
  onRadiusChange: (radius: number) => void;
}) {
  const current = Math.min(Math.max(Number(radius) || 0, 0), 50);
  return (
    <Select
      value={String(current)}
      onValueChange={(v) => onRadiusChange(Number(v))}
      disabled={!hasLocation}
    >
      <SelectTrigger className={`w-auto min-w-[110px] shrink-0 ${filterChipClass(current > 0)}`} title={!hasLocation ? "Pick a city to enable radius" : undefined}>
        <SelectValue placeholder="Radius" />
      </SelectTrigger>
      <SelectContent>
        {RADIUS_OPTIONS.map((mi) => (
          <SelectItem key={mi} value={String(mi)}>{mi === 0 ? "Exact city" : `Within ${mi} mi`}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Shared pill styling for every filter control (Select triggers, chip wrappers)
// so they read as one consistent row instead of mixed default-shadcn + custom.
function filterChipClass(active: boolean) {
  return `h-9 rounded-md border text-[13px] shadow-none transition-colors ${
    active
      ? "border-primary/50 bg-primary/5 text-foreground"
      : "border-border bg-background text-foreground hover:bg-muted"
  }`;
}

// Rendered twice — once in the mobile bar, once at the end of the desktop chip
// row — because the two live in different flex containers. Only one is visible
// at a time and both drive the same `fSort` state.
function SortSelect({
  value,
  onChange,
  hasDistance,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  hasDistance: boolean;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${className ?? ""} ${filterChipClass(false)}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="relevance">Relevance</SelectItem>
        {hasDistance && <SelectItem value="distance">Nearest</SelectItem>}
        <SelectItem value="rating">Highest rating</SelectItem>
        <SelectItem value="reviews">Most reviews</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ToggleChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? "inline-flex items-center justify-center md:justify-start w-full md:w-auto h-9 px-3 text-[13px] rounded-md border border-primary/50 bg-primary/5 text-foreground font-medium shrink-0 transition-colors"
          : "inline-flex items-center justify-center md:justify-start w-full md:w-auto h-9 px-3 text-[13px] rounded-md border border-border bg-background text-foreground hover:bg-muted shrink-0 transition-colors"
      }
    >
      {children}
    </button>
  );
}

function FilterPill({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <span
      className={
        muted
          ? "text-[11px] px-2 py-0.5 rounded-md border border-border text-muted-foreground"
          : "text-[11px] px-2 py-0.5 rounded-md border border-primary/40 text-primary font-medium bg-primary/5"
      }
    >
      {label}
    </span>
  );
}

/**
 * The whole search input: location, free-text query, submit.
 *
 * The query field doubles as a direct-provider lookup — typing two characters
 * fires the same `findProvidersByIdentifier` search the old separate panel
 * used (name, practice, NPI, license #) and offers the matches as jump-to
 * links. Enter still runs a normal search, so the typeahead only ever adds a
 * shortcut; it never intercepts the query.
 */
function SearchBar({
  locationDraft,
  onLocationDraftChange,
  queryDraft,
  onQueryDraftChange,
  onSubmit,
  loading,
  nearMe,
  onUseMyLocation,
  onClearMyLocation,
}: {
  locationDraft: string;
  onLocationDraftChange: (v: string) => void;
  queryDraft: string;
  onQueryDraftChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  nearMe?: boolean;
  onUseMyLocation?: () => void;
  onClearMyLocation?: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const [matches, setMatches] = useState<Provider[]>([]);
  const [looking, setLooking] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const q = queryDraft.trim();
  useEffect(() => {
    if (!focused || q.length < 2) { setMatches([]); return; }
    let cancelled = false;
    setLooking(true);
    const handle = setTimeout(() => {
      findProvidersByIdentifier(q, 6)
        .then((rows) => { if (!cancelled) setMatches(rows); })
        .catch(() => { if (!cancelled) setMatches([]); })
        .finally(() => { if (!cancelled) setLooking(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [q, focused]);

  // Close on outside click rather than on blur, so tapping a suggestion on
  // touch devices doesn't unmount the list before the tap lands.
  useEffect(() => {
    if (!focused) return;
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [focused]);

  const showSuggestions = focused && q.length >= 2;

  // Subscribing here (not in the parent) keeps the re-render local to the bar.
  const geoStatus: GeoStatus = useSyncExternalStore(
    subscribeUserCoords,
    getGeoStatus,
    () => "idle" as GeoStatus,
  );

  return (
    <div ref={boxRef} className="relative">
      <form
        onSubmit={(e) => { setFocused(false); onSubmit(e); }}
        className="flex flex-col sm:flex-row rounded-xl border border-border bg-background divide-y sm:divide-y-0 sm:divide-x divide-border overflow-hidden transition-colors focus-within:border-primary/40"
      >
        <div className="flex items-center gap-2.5 min-w-0 h-11 px-3.5 sm:w-[36%]">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {nearMe ? (
            /* Geo-scoped results need to SAY so and be undoable in one click —
               otherwise an empty location box reads as "searching everywhere"
               while the results are silently scoped to a 25mi circle. */
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className="truncate text-[14px] font-medium text-foreground">Near you</span>
              <button
                type="button"
                onClick={onClearMyLocation}
                aria-label="Stop searching near me"
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : (
            <input
              type="text"
              value={locationDraft}
              onChange={(e) => onLocationDraftChange(e.target.value)}
              placeholder="City, ZIP, or state"
              aria-label="Location"
              className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none"
            />
          )}
          {onUseMyLocation && !nearMe && (
            /* Shared with the hero search — the sticky-denial handling lives in
               the button itself. The hint below still needs geoStatus. */
            <UseMyLocationButton onLocated={() => onUseMyLocation()} />
          )}
        </div>
        <label className="relative flex items-center gap-2.5 min-w-0 flex-1 h-11 px-3.5">
          <SearchIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={queryDraft}
            onChange={(e) => onQueryDraftChange(e.target.value)}
            onFocus={() => setFocused(true)}
            placeholder="Name, specialty, NPI, or a question"
            aria-label="Search providers"
            aria-expanded={showSuggestions}
            className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {(loading || looking) && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" aria-hidden />
          )}
          <button
            type="submit"
            aria-label="Search"
            className="sm:hidden -mr-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </label>
        <button
          type="submit"
          className="hidden sm:inline-flex items-center justify-center gap-2 h-11 px-6 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Search <ArrowRight className="h-4 w-4" />
        </button>
      </form>

      {/* A denial is silent otherwise: the button just never does anything and
          the visitor has no way to know the browser, not the site, said no. */}
      {onUseMyLocation && geoStatus === "denied" && !nearMe && (
        <p className="mt-1.5 text-[12px] text-muted-foreground">
          Location is blocked for this site. Turn it on in your browser's site settings, or type a
          city or ZIP above.
        </p>
      )}

      {showSuggestions && matches.length > 0 && (
        <ul className="absolute z-40 mt-1.5 w-full overflow-hidden rounded-xl border border-border bg-background shadow-lg">
          <li className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
            Jump to provider
          </li>
          {matches.map((p) => (
            <li key={p.id}>
              <Link
                to="/providers/$id"
                params={{ id: buildProviderSlug(p) }}
                onClick={() => setFocused(false)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
              >
                <ProviderPhoto provider={p} className="h-9 w-9 border border-border shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""}
                  </div>
                </div>
              </Link>
            </li>
          ))}
          <li className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
            Press Enter to search all providers for “{q}”
          </li>
        </ul>
      )}
    </div>
  );
}

